import React from "react";
import { EditorView, keymap } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
// import { javascript } from "@codemirror/lang-javascript";
import { markdown } from "@codemirror/lang-markdown";
import { oneDark } from "@codemirror/theme-one-dark";
import { defaultKeymap, indentWithTab } from "@codemirror/commands";
import { history, historyKeymap } from "@codemirror/commands";
import { useTheme } from "@/providers/theme-provider";
import "./code-mirror.css";

// 자식 컴포넌트의 ref 타입 정의
export interface CodeMirrorRefType {
  getContent: () => string;
  setContent: (content: string) => void;
  insertAtCursor: (text: string) => void;
}

// 자식 컴포넌트의 props 타입 정의
interface CodeMirrorProps {
  initialCode: string;
  className: string;
}

export const CodeMirror = React.forwardRef<CodeMirrorRefType, CodeMirrorProps>(
  ({ initialCode, className }, ref) => {
    const { theme } = useTheme();
    const editorRef = React.useRef<HTMLDivElement>(null);
    const editorViewRef = React.useRef<EditorView>();

    React.useEffect(() => {
      if (!editorRef.current) return;

      // 현재 테마가 다크 모드인지 확인
      const isDarkMode =
        theme === "dark" ||
        (theme === "system" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches);
      const themeExtensions = isDarkMode ? [oneDark] : [];

      const view = new EditorView({
        parent: editorRef.current,
        state: EditorState.create({
          doc: initialCode,
          extensions: [
            // javascript(),
            markdown(), // Markdown 언어 지원 추가
            ...themeExtensions, // 테마 확장을 조건부로 적용
            EditorView.lineWrapping,
            EditorView.theme({
              "&": {
                height: "100%",
              },
              ".cm-scroller": {
                fontFamily: "inherit",
              },
              // 라이트 모드를 위한 추가 스타일
              ...(isDarkMode
                ? {}
                : {
                    "&.cm-editor": {
                      backgroundColor: "#ffffff",
                    },
                    ".cm-content": {
                      color: "#000000",
                    },
                    ".cm-cursor": {
                      borderLeftColor: "#000000",
                    },
                    ".cm-selectionBackground": {
                      backgroundColor: "#316ac5 !important",
                    },
                  }),
            }),
            EditorState.allowMultipleSelections.of(true),
            EditorView.editable.of(true),
            EditorState.tabSize.of(2),
            keymap.of([indentWithTab, ...defaultKeymap]),
            history(), // history extension 추가
            keymap.of(historyKeymap), // history keymap 추가
          ],
        }),
      });

      editorViewRef.current = view;

      return () => {
        view.destroy();
      };
    }, [theme]);

    const getContent = () => {
      if (editorViewRef.current) {
        return editorViewRef.current.state.doc.toString();
      }
      return "";
    };

    const setContent = (content: string) => {
      if (editorViewRef.current) {
        const transaction = editorViewRef.current.state.update({
          changes: {
            from: 0,
            to: editorViewRef.current.state.doc.length,
            insert: content,
          },
        });
        editorViewRef.current.dispatch(transaction);
      }
    };

    const insertAtCursor = (text: string) => {
      if (editorViewRef.current) {
        const view = editorViewRef.current;
        const selection = view.state.selection.main;
        const transaction = view.state.update({
          changes: {
            from: selection.from,
            to: selection.to,
            insert: text,
          },
          selection: {
            anchor: selection.from + text.length,
            head: selection.from + text.length,
          },
        });
        view.dispatch(transaction);
        view.focus();
      }
    };

    // 부모 컴포넌트에서 접근할 수 있도록 ref에 함수를 노출
    React.useImperativeHandle(ref, () => ({
      getContent,
      setContent,
      insertAtCursor,
    }));

    return (
      <div
        ref={editorRef}
        className={`codemirror-container w-full h-full ${className}`}
      />
    );
  },
);
